import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, useLocalization } from '@ui-framework/index';
import {
  ButtonContainer,
  IconLibrary,
  Layout,
  PageSection,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import VariantForm from '@product-framework/components/AdvancedForms/FormDialog';
import ConfirmationPopUp from '@ui-framework/components/UI/ConfirmationPopUp';
import usePresignedUploads from '@product-framework/components/DataProvider/usePresignedUploads';
import {
  createVariant,
  deleteDocument,
  deleteVariant,
  getDocument,
  metadataPayload,
  metadataValues,
  pendingUploads,
  requireData,
  updateDocument,
  updateVariant,
  variantPayload,
} from '../../api';
import editSchema from '../../schemas/edit.json';
import { uploadSchema, variantSchema } from '../../forms/schemas';
import {
  metadataValidationSchema,
  variantValidationSchema,
} from '../../forms/validations';
import useDocumentSettings from '../../useDocumentSettings';

const FORM_ID = 'DOCUMENT_METADATA_FORM';
function DocumentDetail({ id }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    settings,
    error: settingsError,
    refetch,
    dataProvider,
  } = useDocumentSettings();
  const [document, setDocument] = useState(null);
  const [metadataDocument, setMetadataDocument] = useState(null);
  const documentRef = useRef(null);
  documentRef.current = document;
  const [error, setError] = useState(location.state?.uploadError || null);
  const [loadError, setLoadError] = useState(false);
  const loadErrorMessage = useRef(null);
  const [formRevision, setFormRevision] = useState(0);
  const [variantEditor, setVariantEditor] = useState(null);
  const savedVariantId = useRef(null);
  const savedLanguageCode = useRef(null);
  const formRef = useRef();
  const confirmationRef = useRef();
  const mounted = useRef(true);
  const uploads = usePresignedUploads();
  const load = useCallback(async () => {
    try {
      const result = requireData(await getDocument(dataProvider, id));
      if (mounted.current) {
        setDocument(result);
        setMetadataDocument(current => current || result);
        // Retiring the alert with its Retry button, without hiding an
        // unrelated upload or save error.
        if (loadErrorMessage.current !== null) {
          const staleMessage = loadErrorMessage.current;
          loadErrorMessage.current = null;
          setError(current => (current === staleMessage ? null : current));
        }
        setLoadError(false);
      }
      return result;
    } catch (failure) {
      if (mounted.current) {
        loadErrorMessage.current = failure.message;
        setError(failure.message);
        setLoadError(true);
      }
      return null;
    }
  }, [dataProvider, id]);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
  }, [load]);
  const metadataSchema = useMemo(
    () =>
      settings &&
      metadataDocument &&
      uploadSchema(editSchema, settings, metadataDocument),
    [settings, metadataDocument],
  );
  const languageSchema = useMemo(
    () =>
      settings &&
      variantEditor &&
      uploadSchema(
        variantSchema,
        settings,
        variantEditor.variant,
        variantEditor.languages,
        Boolean(variantEditor.variant.id),
      ),
    [settings, variantEditor],
  );
  const metadataDefaults = useMemo(
    () => metadataDocument && metadataValues(metadataDocument),
    [metadataDocument],
  );
  const variantDefaults = useMemo(
    () => ({
      languageCode: '',
      title: '',
      description: '',
      pdfFile: null,
      videoFile: null,
      ...variantEditor?.variant,
    }),
    [variantEditor],
  );
  const metadataValidation = useMemo(
    () => metadataValidationSchema(formatMessage),
    [formatMessage],
  );
  const variantValidation = useMemo(
    () => variantValidationSchema(formatMessage),
    [formatMessage],
  );
  const failureResult = (failure, values) => {
    if (mounted.current) setError(failure.message);
    return {
      nextValues: values,
      errors: failure.errors || [{ message: failure.message }],
    };
  };
  const saveMetadata = async values => {
    setError(null);
    try {
      let updated = requireData(
        await updateDocument(dataProvider, id, {
          ...metadataPayload(values),
          recordVersion: documentRef.current.recordVersion,
        }),
      );
      if (mounted.current) setDocument(updated);
      const results = await uploads.runUploads(
        pendingUploads(dataProvider, id, null, values),
      );
      updated = results[results.length - 1] || updated;
      if (mounted.current) setDocument(updated);
      return {
        nextValues: metadataValues(updated),
        errors: null,
        callback: () => {
          setMetadataDocument(updated);
          setFormRevision(value => value + 1);
        },
      };
    } catch (failure) {
      const refreshed = await load();
      if (failure.statusCode === 409 && mounted.current && refreshed) {
        setMetadataDocument(refreshed);
        setFormRevision(value => value + 1);
      }
      return failureResult(failure, values);
    }
  };
  const openVariant = (variant = {}) => {
    savedVariantId.current = variant.id || null;
    savedLanguageCode.current = variant.languageCode || null;
    setVariantEditor({
      variant,
      languages: document.variants.map(item => item.languageCode),
    });
  };
  const saveVariant = async values => {
    setError(null);
    try {
      const text = variantPayload({
        ...values,
        languageCode: savedLanguageCode.current || values.languageCode,
      });
      const updated = requireData(
        await (savedVariantId.current
          ? updateVariant(dataProvider, id, savedVariantId.current, {
              title: text.title,
              description: text.description,
            })
          : createVariant(dataProvider, id, text)),
      );
      savedLanguageCode.current = text.languageCode;
      savedVariantId.current = updated.variants.find(
        item => item.languageCode === text.languageCode,
      ).id;
      if (!mounted.current) return { nextValues: values, errors: null };
      if (mounted.current) setDocument(updated);
      const results = await uploads.runUploads(
        pendingUploads(dataProvider, id, savedVariantId.current, values),
      );
      if (mounted.current) setDocument(results[results.length - 1] || updated);
      return {
        nextValues: { ...values, pdfFile: null, videoFile: null },
        errors: null,
        callback: () => setVariantEditor(null),
      };
    } catch (failure) {
      const refreshed = await load();
      if (
        mounted.current &&
        savedVariantId.current &&
        !variantEditor.variant.id
      ) {
        const saved = refreshed?.variants.find(
          item => item.id === savedVariantId.current,
        );
        setVariantEditor(current => ({
          ...current,
          variant: {
            ...values,
            ...saved,
            id: savedVariantId.current,
            languageCode: savedLanguageCode.current,
          },
        }));
      }
      return failureResult(failure, values);
    }
  };
  const confirmDelete = variant => {
    confirmationRef.current.init({
      title: formatMessage({ id: 'DELETE', defaultMessage: 'Delete' }),
      message: formatMessage(
        variant
          ? {
              id: 'DELETE_THIS_LANGUAGE_VERSION',
              defaultMessage: 'Delete this language version?',
            }
          : {
              id: 'DELETE_THIS_DOCUMENT',
              defaultMessage: 'Delete this document?',
            },
      ),
      positiveButtonText: formatMessage({
        id: 'DELETE',
        defaultMessage: 'Delete',
      }),
      negativeButtonText: formatMessage({
        id: 'CANCEL',
        defaultMessage: 'Cancel',
      }),
      onChange: async accepted => {
        if (accepted) {
          try {
            const response = await (variant
              ? deleteVariant(dataProvider, id, variant.id)
              : deleteDocument(dataProvider, id));
            const updated = requireData(response);
            if (variant) setDocument(updated);
            else navigate('/document-management');
          } catch (failure) {
            if (failure.statusCode === 409) await load();
            failureResult(failure, {});
          }
        }
        confirmationRef.current?.setOpen(false);
      },
    });
    confirmationRef.current.setOpen(true);
  };
  return (
    <Layout>
      <Breadcrumb
        items={[
          {
            name: formatMessage({
              id: 'DOCUMENT_MANAGEMENT',
              defaultMessage: 'Document Management',
            }),
            icon: IconLibrary.Help,
            onClick: () => navigate('/document-management'),
          },
          {
            name:
              document?.variants.find(variant => variant.languageCode === 'en')
                ?.title ||
              document?.variants[0]?.title ||
              id,
            icon: IconLibrary.Note,
          },
        ]}
      />
      {(!document || !settings) && !loadError && !settingsError && (
        <LinearProgress />
      )}
      {uploads.uploading && (
        <LinearProgress
          variant="determinate"
          value={uploads.progress?.percent || 0}
          aria-label={uploads.progress?.fileName}
        />
      )}
      {uploads.uploading && (
        <ButtonContainer>
          <Button onClick={uploads.cancel}>
            {formatMessage({
              id: 'CANCEL_UPLOAD',
              defaultMessage: 'Cancel upload',
            })}
          </Button>
        </ButtonContainer>
      )}
      {error && (
        <Alert
          severity="error"
          action={
            loadError && (
              <Button onClick={load}>
                {formatMessage({ id: 'RETRY', defaultMessage: 'Retry' })}
              </Button>
            )
          }>
          {error}
        </Alert>
      )}
      {settingsError && (
        <Alert
          severity="error"
          action={
            <Button onClick={refetch}>
              {formatMessage({ id: 'RETRY', defaultMessage: 'Retry' })}
            </Button>
          }>
          {formatMessage({
            id: 'UPLOAD_SETTINGS_COULD_NOT_BE_LOADED',
            defaultMessage: 'Upload settings could not be loaded.',
          })}
        </Alert>
      )}
      {document && settings && (
        <>
          {!document.variants.some(
            variant => variant.languageCode === 'en',
          ) && (
            <Alert severity="warning">
              {formatMessage({
                id: 'DOCUMENT_MANAGEMENT_NO_ENGLISH',
                defaultMessage:
                  'This document has no English language version. Readers without a matching language may not see it.',
              })}
            </Alert>
          )}
          <PageSection
            label={formatMessage({
              id: 'DOCUMENT_DETAILS',
              defaultMessage: 'Document Details',
            })}>
            <Form
              key={formRevision}
              formId={`${FORM_ID}_${formRevision}`}
              ref={formRef}
              permittedByDefault
              schema={metadataSchema}
              defaultValues={metadataDefaults}
              validationSchema={metadataValidation}
              onSubmit={saveMetadata}>
              <ButtonContainer>
                <Button
                  variant="contained"
                  disabled={uploads.uploading || Boolean(variantEditor)}
                  onClick={() =>
                    formRef.current?.submit({
                      formId: `${FORM_ID}_${formRevision}`,
                    })
                  }>
                  {formatMessage({ id: 'SAVE', defaultMessage: 'Save' })}
                </Button>
                <Button
                  color="error"
                  disabled={document.status !== 'INACTIVE' || uploads.uploading}
                  onClick={() => confirmDelete()}>
                  {formatMessage({ id: 'DELETE', defaultMessage: 'Delete' })}
                </Button>
              </ButtonContainer>
            </Form>
          </PageSection>
          <PageSection
            label={formatMessage({
              id: 'LANGUAGES',
              defaultMessage: 'Languages',
            })}>
            <Button onClick={() => openVariant()} disabled={uploads.uploading}>
              {formatMessage({
                id: 'ADD_LANGUAGE_VERSION',
                defaultMessage: 'Add Language Version',
              })}
            </Button>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    ['DOCUMENT_MANAGEMENT_LANGUAGE', 'Language'],
                    ['DOCUMENT_MANAGEMENT_TITLE_LABEL', 'Title'],
                    ['DOCUMENT_MANAGEMENT_PDFFILE_LABEL', 'PDF'],
                    ['DOCUMENT_MANAGEMENT_VIDEOFILE_LABEL', 'Video'],
                    ['DOCUMENT_MANAGEMENT_LAST_MODIFIED', 'Last modified'],
                    ['ACTIONS', 'Actions'],
                  ].map(([localeId, defaultMessage]) => (
                    <TableCell key={localeId}>
                      {formatMessage({ id: localeId, defaultMessage })}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {document.variants.map(variant => (
                  <TableRow key={variant.id}>
                    <TableCell>{variant.languageCode.toUpperCase()}</TableCell>
                    <TableCell>{variant.title}</TableCell>
                    {['pdf', 'video'].map(kind => (
                      <TableCell key={kind}>
                        {variant[kind] && (
                          <Link
                            href={variant[kind].url}
                            target="_blank"
                            rel="noopener noreferrer">
                            {variant[kind].fileName}
                          </Link>
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      {variant.lastModifiedDateTime
                        ? new Date(
                            variant.lastModifiedDateTime,
                          ).toLocaleString()
                        : ''}
                    </TableCell>
                    <TableCell>
                      <Button
                        disabled={uploads.uploading}
                        onClick={() => openVariant(variant)}>
                        {formatMessage({ id: 'EDIT', defaultMessage: 'Edit' })}
                      </Button>
                      <Button
                        color="error"
                        disabled={uploads.uploading}
                        onClick={() => confirmDelete(variant)}>
                        {formatMessage({
                          id: 'DELETE',
                          defaultMessage: 'Delete',
                        })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PageSection>
          {variantEditor && (
            <VariantForm
              key={variantEditor.variant.id || 'new'}
              formId="DOCUMENT_VARIANT_FORM"
              title={formatMessage(
                variantEditor.variant.id
                  ? {
                      id: 'EDIT_LANGUAGE_VERSION',
                      defaultMessage: 'Edit Language Version',
                    }
                  : {
                      id: 'ADD_LANGUAGE_VERSION',
                      defaultMessage: 'Add Language Version',
                    },
              )}
              onClose={() => setVariantEditor(null)}
              onCancelSaving={uploads.uploading ? uploads.cancel : undefined}
              onSubmit={saveVariant}
              permittedByDefault
              schema={languageSchema}
              defaultValues={variantDefaults}
              validationSchema={variantValidation}
            />
          )}
        </>
      )}
      <ConfirmationPopUp reference={confirmationRef} />
    </Layout>
  );
}
export default function DocumentManagementDetail() {
  const { id } = useParams();
  return <DocumentDetail key={id} id={id} />;
}
